import type { SearchSchemaInput } from '@tanstack/react-router';

/**
 * عقد `search params` لشاشة الإعدادات (§6.9) — **معزول في ملف بلا مكوّنات، وعزله
 * قرار ميزانية لا ترتيب ملفات.**
 *
 * السبب المقيس: `validateSearch` ليس مكوّنًا بل **خيار مسار** يحتاجه الموجّه وهو
 * يبني الشجرة قبل أي تنقّل، فـ `autoCodeSplitting` يؤجّل `component` ويُبقي هذا
 * الخيار في الحزمة الابتدائية إلزامًا. ووحدة التقسيم في Rollup هي **الملف** لا
 * التصريح: فحين كان المدقّق ساكنًا في `SettingsScreen.tsx` كان استيراده وحده يسحب
 * الملف كله إلى المدخل، ومعه شجرته: تبويبات الإعدادات و`settings/ui` و`today/ui`
 * و`auth/webauthn` و`radix-ui` (Tabs · Switch · AlertDialog) و**`motion` بـ 120KB
 * خامًا** — أي أن المالك كان يحمّل شاشة الإعدادات كي يرى صندوقه.
 *
 * القاعدة الناتجة والملزمة لكل مسار: **لا خيار مسار يسكن ملفًا فيه مكوّن.**
 */

export const SETTINGS_TABS = [
  { id: 'general', label: 'عام' },
  /* الذكاء بعد «عام» مباشرةً: أكثر ما يبدّله المالك تكرارًا (المزوّد يتغيّر بالسعر). */
  { id: 'ai', label: 'الذكاء' },
  { id: 'stores', label: 'المتاجر' },
  { id: 'appearance', label: 'المظهر' },
  { id: 'sessions', label: 'الجلسات' },
  { id: 'tokens', label: 'الرموز' },
] as const;

export type SettingsTabId = (typeof SETTINGS_TABS)[number]['id'];

export const TAB_IDS: readonly SettingsTabId[] = SETTINGS_TABS.map((tab) => tab.id);

export const DEFAULT_SETTINGS_TAB: SettingsTabId = 'general';

export interface SettingsSearch {
  readonly tab: SettingsTabId;
  readonly commerce?: 'connected' | 'error';
  readonly detail?: string;
}

/** الدخل بعلامة الموجّه: كل حقوله اختيارية فيبقى `<Link to="/settings">` بلا search. */
export type SettingsSearchInput = {
  readonly tab?: unknown;
  readonly commerce?: unknown;
  readonly detail?: unknown;
} & SearchSchemaInput;

/** يتسامح مع أي قيمة في الـ URL: تبويب مجهول يعود إلى «عام» بلا شاشة خطأ. */
export function validateSettingsSearch(input: SettingsSearchInput): SettingsSearch {
  const commerce = input['commerce'] === 'connected' || input['commerce'] === 'error'
    ? input['commerce']
    : undefined;
  const detail = typeof input['detail'] === 'string' && input['detail'].length <= 500
    ? input['detail']
    : undefined;
  return {
    tab: TAB_IDS.find((id) => id === input.tab) ?? DEFAULT_SETTINGS_TAB,
    ...(commerce === undefined ? {} : { commerce }),
    ...(detail === undefined ? {} : { detail }),
  };
}
